import { useState, createContext, useContext } from 'react'
import { OrderTypeCapacity } from 'components/Locations/LocationQueries'

type CachedCapacity = {
  capacity: OrderTypeCapacity[]
  isStale: boolean
}

type StoreOrderTypeCapacityState = {
  storeOrderTypeCapacityByStore: Record<string, CachedCapacity>
  setStoreOrderTypeCapacityForStore: (
    storeId: string,
    capacity: OrderTypeCapacity[]
  ) => void
  markStoreOrderTypeCapacityStale: (storeId: string) => void
}

export const SetupStoreOrderTypeCapacity = (): StoreOrderTypeCapacityState => {
  const [storeOrderTypeCapacityByStore, setStoreOrderTypeCapacityByStore] =
    useState<Record<string, CachedCapacity>>({})

  const setStoreOrderTypeCapacityForStore = (
    storeId: string,
    capacity: OrderTypeCapacity[]
  ) =>
    setStoreOrderTypeCapacityByStore((prev) => ({
      ...prev,
      [storeId]: { capacity, isStale: false }
    }))

  const markStoreOrderTypeCapacityStale = (storeId: string) =>
    setStoreOrderTypeCapacityByStore((prev) => {
      const cached = prev[storeId]
      if (!cached) return prev

      return { ...prev, [storeId]: { ...cached, isStale: true } }
    })

  return {
    storeOrderTypeCapacityByStore,
    setStoreOrderTypeCapacityForStore,
    markStoreOrderTypeCapacityStale
  }
}

const initialState: StoreOrderTypeCapacityState = {
  storeOrderTypeCapacityByStore: {},
  setStoreOrderTypeCapacityForStore: () => null,
  markStoreOrderTypeCapacityStale: () => null
}

export const StoreOrderTypeCapacityContext =
  createContext<StoreOrderTypeCapacityState>(initialState)

const useStoreOrderTypeCapacityContext = () => {
  return useContext(StoreOrderTypeCapacityContext)
}

// Caches order-type capacity per store so navigating between locations, or
// remounting a loader for the same location, doesn't force a refetch.
//
// Invalidation is by store id, not by a stashed Apollo refetch() closure -
// a single shared "current refetch" function can't tell which store a
// mutation actually affected once the loader has moved on to a different
// store (e.g. the user navigates away before a save's .finally() runs), so
// it would silently refetch the wrong store and leave the mutated one stale.
//
// It marks the entry stale rather than deleting it, so cached capacity stays
// readable while the refetch is in flight. That keeps hasCapacity - and so the
// loader's gate - true across a save, instead of unmounting the page the user
// just saved from.
export const useStoreOrderTypeCapacity = (storeId?: string) => {
  const {
    storeOrderTypeCapacityByStore,
    setStoreOrderTypeCapacityForStore,
    markStoreOrderTypeCapacityStale
  } = useStoreOrderTypeCapacityContext()

  const cached = storeId ? storeOrderTypeCapacityByStore[storeId] : undefined

  return {
    capacity: cached?.capacity || [],
    // Whether there is capacity to render at all - drives the loader's gate.
    hasCapacity: !!cached,
    // Whether that capacity is missing or out of date - drives the fetch.
    needsFetch: !!storeId && (!cached || cached.isStale),
    setCapacity: (capacity: OrderTypeCapacity[]) =>
      storeId && setStoreOrderTypeCapacityForStore(storeId, capacity),
    invalidate: () => storeId && markStoreOrderTypeCapacityStale(storeId)
  }
}
